import {
  WORK_QUEUE_ROUTING_METHOD_LABELS,
  type WorkQueue,
  type WorkQueueEntityType,
  type WorkQueueRoutingMethod,
  type WorkQueueRoutingOptions,
} from './work-queues-types';

/** Editable form state for the Create / Edit Work Queue screen. */
export interface WorkQueueDraft {
  name: string;
  description: string;
  entity_type: string;
  team_role: string;
  member_ids: string[];
  routing_method: WorkQueueRoutingMethod | '';
  routing_options: WorkQueueRoutingOptions;
  sla_hours: number | null;
  escalation_hours: number | null;
  escalation_role: string;
  send_reminders: boolean;
  is_active: boolean;
}

const DEFAULT_SLA_HOURS = 48;
const DEFAULT_ESCALATION_HOURS = 72;

export const DEFAULT_ROUTING_OPTIONS: WorkQueueRoutingOptions = {
  // Roles come from the API, so there's no safe default role key
  assigner_role: '',
  unassigned_alert_hours: 4,
  allow_self_assign: false,
  notify_assignee: true,
  skip_out_of_office: true,
  max_open_per_member: 15,
  claim_order: 'oldest_first',
  release_after_idle_minutes: 15,
  min_confidence_percent: 80,
  rules: [],
  territories: {},
};

export function createEmptyWorkQueueDraft(): WorkQueueDraft {
  return {
    name: '',
    description: '',
    entity_type: '',
    team_role: '',
    member_ids: [],
    routing_method: '',
    routing_options: { ...DEFAULT_ROUTING_OPTIONS, rules: [], territories: {} },
    sla_hours: DEFAULT_SLA_HOURS,
    escalation_hours: DEFAULT_ESCALATION_HOURS,
    escalation_role: '',
    send_reminders: true,
    is_active: true,
  };
}

export function workQueueToDraft(queue: WorkQueue): WorkQueueDraft {
  return {
    name: queue.name,
    description: queue.description,
    entity_type: queue.entity_type,
    team_role: queue.team_role,
    member_ids: queue.members.map((member) => member.user_id),
    routing_method: queue.routing_method,
    routing_options: {
      ...DEFAULT_ROUTING_OPTIONS,
      ...queue.routing_options,
      rules: queue.routing_options?.rules?.map((rule) => ({ ...rule })) ?? [],
      territories: { ...queue.routing_options?.territories },
    },
    sla_hours: queue.sla_hours,
    escalation_hours: queue.escalation_hours,
    escalation_role: queue.escalation_role,
    send_reminders: queue.send_reminders,
    is_active: queue.is_active,
  };
}

export function isWorkQueueDraftComplete(draft: WorkQueueDraft): boolean {
  return Boolean(
    draft.name.trim() &&
      draft.entity_type &&
      draft.team_role &&
      draft.member_ids.length &&
      draft.routing_method
  );
}

export type WorkQueueDraftSummary =
  | {
      complete: true;
      entityName: string;
      methodLabel: string;
      memberCount: number;
    }
  | { complete: false; missing: string[] };

export function getWorkQueueDraftSummary(
  draft: WorkQueueDraft,
  entityTypes: WorkQueueEntityType[]
): WorkQueueDraftSummary {
  if (isWorkQueueDraftComplete(draft)) {
    const entityType = entityTypes.find(
      (entity) => entity.uuid === draft.entity_type
    );
    return {
      complete: true,
      entityName: entityType?.name ?? draft.entity_type,
      methodLabel:
        WORK_QUEUE_ROUTING_METHOD_LABELS[
          draft.routing_method as WorkQueueRoutingMethod
        ],
      memberCount: draft.member_ids.length,
    };
  }

  const missing: string[] = [];
  if (!draft.name.trim()) missing.push('a name');
  if (!draft.entity_type) missing.push('an entity type');
  if (!draft.team_role) missing.push('a team role');
  else if (!draft.member_ids.length) missing.push('team members');
  if (!draft.routing_method) missing.push('a routing method');
  return { complete: false, missing };
}

/** Parses a numeric input value, keeping an empty field as null so it can be cleared while typing. */
export function parseNumberInput(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

let ruleIdCounter = 0;

/** Client-side id for a rule added in the editor; replaced by the API id once persisted. */
export function createRuleId(): string {
  ruleIdCounter += 1;
  return `rule-new-${Date.now()}-${ruleIdCounter}`;
}

export function getInitials(name: string): string {
  const parts = name
    .replace(/@.*/, '')
    .split(/[\s.]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
